import { DatePipe, getLocaleFirstDayOfWeek, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { NgbTimeAdapter, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { es } from 'date-fns/locale/es';
import { LocalizeService } from '../pipes/localize.service';
import { Layout } from '../../layout/layout.types';

registerLocaleData(localeEs, 'es-ES');

// Types
export type Scheme = 'auto' | 'dark' | 'light';
export type Screens = { [key: string]: string };
export type Theme = 'theme-default' | string;
export type Themes = { id: string; name: string }[];

/**
 * AppConfig interface. Update this interface to strictly type your config
 * object.
 */
export interface AppConfig
{
    layout: Layout;
    scheme: Scheme;
    screens: Screens;
    theme: Theme;
    themes: Themes;
}

/**
 * Default configuration for the entire application. This object is used by
 * FuseConfigService to set the default configuration.
 *
 * If you need to store global configuration for your app, you can use this
 * object to set the defaults. To access, update and reset the config, use
 * FuseConfigService and its methods.
 *
 * "Screens" are carried over to the BreakpointObserver for accessing them within
 * components, and they are required.
 *
 * "Themes" are required for Tailwind to generate themes.
 */
export const appConfig: AppConfig = {
    layout : 'modern',
    scheme : 'light',
    screens: {
        'sm': '600px',
        'md': '960px',
        'lg': '1280px',
        'xl': '1440px',
        '2xl': '1680px',
        '3xl': '1920px',
        '4xl': '2160px',
        '5xl': '2400px',
        '6xl': '2560px',
    },
    theme  : 'theme-default',
    themes : [
        {
            id  : 'theme-default',
            name: 'Default'
        },
        {
            id  : 'theme-brand',
            name: 'Brand'
        },
        {
            id  : 'theme-teal',
            name: 'Teal'
        },
        {
            id  : 'theme-rose',
            name: 'Rose'
        },
        {
            id  : 'theme-purple',
            name: 'Purple'
        },
        {
            id  : 'theme-amber',
            name: 'Amber'
        },
        {
            id: 'theme-gesgrup',
            name: 'Gesgrup'
        }
    ]
};

export const langDefinition = {
    'es-ES': { id: 'es-ES', label: 'Español' },
    'en-GB': { id: 'en-GB', label: 'English' },
    'pt-PT': { id: 'pt-PT', label: 'Português' }
};

@Injectable()
export class NgbTimeStringAdapter extends NgbTimeAdapter<string> {

    pad = (i: number): string => i < 10 ? `0${i}` : `${i}`;

    fromModel(value: string | null): NgbTimeStruct | null {
        if (!value) {
            return null;
        }
        const split = value.split(':');
        return {
            hour: parseInt(split[0], 10),
            minute: parseInt(split[1], 10),
            second: parseInt(split[2], 10)
        };
    }

    toModel(time: NgbTimeStruct | null): string | null {
        return time != null ? `${this.pad(time.hour)}:${this.pad(time.minute)}:${this.pad(time.second)}` : null;
    }
}

@Injectable({ providedIn: 'root' })
export class CustomDateAdapter extends NativeDateAdapter {
    constructor(
    ) {
        super();
    }

    getFirstDayOfWeek() {
        return es.options.weekStartsOn;
    }
}

@Injectable({ providedIn: 'root' })
export class TimezoneDateAdapter extends NativeDateAdapter {

    constructor(
        @Inject(LOCALE_ID) public locale,
        private localizeService: LocalizeService,
    ) {
        super();
    }

    format(date: Date) {
        return this.localizeService.transformTimezoneDate(date);
    }
}
